import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import MuiAccordion, { type AccordionProps } from '@mui/material/Accordion'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { PencilAlt } from '@styled-icons/heroicons-outline/PencilAlt'
import { Plus } from '@styled-icons/heroicons-outline/Plus'
import { noop } from 'lodash'
import React, { useCallback } from 'react'

type StyledAccordionProps = {
  isExpandCollapseHeightSame?: boolean
}

// disableGutters + elevation=0 must stay explicit here — MuiAccordion is a Paper under the hood,
// so without them it adds its own default margin-on-expand and box-shadow on top of the ones below.
const Accordion = styled(({ isExpandCollapseHeightSame: _isExpandCollapseHeightSame, ...rest }: StyledAccordionProps & AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} {...rest} />
))<StyledAccordionProps>(({ theme, isExpandCollapseHeightSame }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.border.secondary}`,
  boxSizing: 'border-box',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
  borderRadius: theme.shape.borderRadius + 2,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root.Mui-expanded': {
    minHeight: theme.spacing(isExpandCollapseHeightSame ? 6 : 5),
  },
}))

type AccordionSummaryCustomProps = {
  withNewColors?: boolean
}

export const AccordionSummary = styled(MuiAccordionSummary, {
  shouldForwardProp: prop => prop !== 'withNewColors',
})<AccordionSummaryCustomProps>(({ theme, withNewColors = false }) => ({
  backgroundColor: withNewColors ? theme.palette.background.header : theme.palette.background.accordion,
  borderRadius: `${theme.shape.borderRadius + 2}px ${theme.shape.borderRadius + 2}px 0 0`,
  height: theme.spacing(5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  borderBottomColor: theme.palette.border.secondary,
  borderBottomWidth: 1,
  borderBottomStyle: 'solid',
  flexDirection: 'row-reverse',
  alignItems: 'center',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  '&.Mui-focusVisible': {
    backgroundColor: withNewColors ? theme.palette.background.header : theme.palette.background.accordion,
  },
}))

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: 0,
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
  borderRadius: 1.5,
  display: 'block',
}))

const ACTION_TO_ICON_MAP = {
  edit: PencilAlt,
  add: Plus,
  ellipsis: MoreVertIcon,
} as const

type ActionType = keyof typeof ACTION_TO_ICON_MAP

type HHAccordionProps = Omit<AccordionProps, 'children'> & {
  showActionIcon?: boolean
  summary?: React.ReactNode
  actionType?: ActionType | string
  onActionClick?: (event: React.MouseEvent) => void
  isExpanded?: boolean
  isExpandCollapseHeightSame?: boolean
  children?: React.ReactNode
  withNewColors?: boolean
  showCustomActions?: boolean
  customActionContent?: React.ReactNode
}

const HHAccordion: React.FC<HHAccordionProps> = ({
  showActionIcon = true,
  summary = 'Title',
  actionType = 'edit',
  onActionClick = noop,
  isExpanded = false,
  isExpandCollapseHeightSame = false,
  children,
  withNewColors = false,
  showCustomActions = false,
  customActionContent,
  ...rest
}) => {
  const [expanded, setExpanded] = React.useState(isExpanded)
  const ActionIcon = actionType in ACTION_TO_ICON_MAP ? ACTION_TO_ICON_MAP[actionType as ActionType] : Plus

  const handleAccordionChange = useCallback(() => {
    setExpanded(isOpen => !isOpen)
  }, [])

  const handleActionIconClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      onActionClick(event)
    },
    [onActionClick]
  )

  return (
    <Accordion expanded={expanded} isExpandCollapseHeightSame={isExpandCollapseHeightSame} onChange={handleAccordionChange} {...rest}>
      {/* Not a native button: the browser would claim the space key, and a field in here needs it. */}
      <AccordionSummary
        component="div"
        direction="row-reverse"
        expandIcon={<ArrowRightIcon sx={{ fontSize: '1.5rem', color: '#afb4bd' }} />}
        withNewColors={withNewColors}
      >
        <Typography color="text.primary" fontSize={14} variant="h4">
          {summary}
        </Typography>
        {showActionIcon && (
          <IconButton sx={{ color: 'text.secondary' }} onClick={handleActionIconClick}>
            <ActionIcon height={20} width={20} />
          </IconButton>
        )}
        {showCustomActions && customActionContent}
      </AccordionSummary>
      <AccordionDetails>
        <Box display="block" width="100%">
          {children}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default HHAccordion
